import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, ReactNode } from 'react';
import { authApi } from '../services/api';
import {
  activateMasterTestingSession,
  clearMasterTestingSession,
  isMasterSandboxUnlocked,
  isMasterTestingKey,
} from '../lib/masterTestingKey';
import { isPublicAppPath } from '../lib/publicRoutes';

interface User {
  userToken: string;
  role: 'PRO' | 'ELT' | 'ORG';
  accountState: string;
  successGuideId: string | null;
  introCompleted: boolean;
  name?: string;
  proteusMode?: string;
  testOverride?: boolean;
  masterTestingOverride?: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  chatEnabled: boolean;
  masterSandboxUnlocked: boolean;
  setChatEnabled: (enabled: boolean) => void;
  login: (key: string) => Promise<{
    user?: User;
    testOverride?: boolean;
    masterTestingOverride?: boolean;
    chatEnabled?: boolean;
  }>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [chatEnabled, setChatEnabled] = useState(false);
  const sessionBootRef = useRef(false);
  const refreshInFlightRef = useRef(false);

  useEffect(() => {
    if (sessionBootRef.current) return;
    sessionBootRef.current = true;

    const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';

    // Guest / public surfaces (incl. Screen-PRO08X) must not require /api/auth/me.
    if (isPublicAppPath(pathname)) {
      setUser(null);
      setChatEnabled(false);
      setLoading(false);
      return;
    }

    authApi.me()
      .then((data) => {
        setUser(data.user);
        setChatEnabled(data.chatEnabled);
        if (data.masterTestingOverride || data.user?.masterTestingOverride) {
          activateMasterTestingSession();
        }
      })
      .catch(() => {
        setUser(null);
        setChatEnabled(false);
      })
      .finally(() => setLoading(false));
  }, []);

  const refresh = useCallback(async () => {
    if (refreshInFlightRef.current) return;
    refreshInFlightRef.current = true;

    try {
      const data = await authApi.me();
      setUser(data.user);
      setChatEnabled(data.chatEnabled);
      if (data.masterTestingOverride || data.user?.masterTestingOverride) {
        activateMasterTestingSession();
      }
    } catch {
      setUser(null);
      setChatEnabled(false);
    } finally {
      setLoading(false);
      refreshInFlightRef.current = false;
    }
  }, []);

  const login = useCallback(async (key: string) => {
    const data = await authApi.login(key);
    if (data.masterTestingOverride || isMasterTestingKey(key)) {
      activateMasterTestingSession();
    }
    setUser(data.user);
    setChatEnabled(Boolean(data.chatEnabled ?? data.user?.testOverride ?? data.masterTestingOverride));
    setLoading(false);
    return data;
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    clearMasterTestingSession();
    setUser(null);
    setChatEnabled(false);
  }, []);

  const masterSandboxUnlocked = isMasterSandboxUnlocked(user);

  const value = useMemo(
    () => ({
      user,
      loading,
      chatEnabled,
      masterSandboxUnlocked,
      setChatEnabled,
      login,
      logout,
      refresh,
    }),
    [user, loading, chatEnabled, masterSandboxUnlocked, login, logout, refresh],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
